import { SyncStatus, getSyncStatus } from "./syncStatus";

export const GametimeListingStatus = {
	Available: "available",
	Matched: "matched",
	Unmatched: "unmatched",
	Invalid: "invalid",
	Deleted: "deleted",
} as const;

export type TGametimeStatusTone = "live" | "waiting" | "error" | "removed" | "notSynced";

export type TGametimeListingStatus = {
	label: string;
	tone: TGametimeStatusTone;
	isError: boolean;
	isUnmatched: boolean;
};

export type TGametimeListingError = {
	code?: string | null;
	message?: string | null;
	field?: string | null;
	value?: string | null;
};

export type TGametimeStatusFilter = "all" | "unmatched" | "errors";

const NOT_SYNCED: TGametimeListingStatus = { label: "Not synced", tone: "notSynced", isError: false, isUnmatched: false };

const STATUSES: Record<string, TGametimeListingStatus> = {
	[GametimeListingStatus.Available]: { label: "Live on Gametime", tone: "live", isError: false, isUnmatched: false },
	[GametimeListingStatus.Matched]: { label: "Live on Gametime", tone: "live", isError: false, isUnmatched: false },
	[GametimeListingStatus.Unmatched]: {
		label: "Waiting for Gametime to match the event",
		tone: "waiting",
		isError: false,
		isUnmatched: true,
	},
	[GametimeListingStatus.Invalid]: {
		label: "Rejected by Gametime — needs fixing",
		tone: "error",
		isError: true,
		isUnmatched: false,
	},
	[GametimeListingStatus.Deleted]: { label: "Removed from Gametime", tone: "removed", isError: false, isUnmatched: false },
};

export const getGametimeListingStatus = (status: string | null | undefined): TGametimeListingStatus => {
	const key = (status ?? "").trim().toLowerCase();
	if (!key) return NOT_SYNCED;
	return STATUSES[key] ?? { label: `Unrecognised Gametime status (${key})`, tone: "error", isError: true, isUnmatched: false };
};

const ERROR_HINTS: Record<string, string> = {
	INVALID_IN_HAND_DATE: "The in-hand date is not accepted — check it is set and before the event.",
	BANNED_ATTRIBUTE: "The listing notes contain a term Gametime does not allow — edit the external notes.",
	INVALID_PRICE: "The price is not accepted — check the listing price and markup.",
	INVALID_QUANTITY: "The quantity or split is not accepted — check the quantity and split rules.",
	INVALID_SEATS: "The seat numbers are not accepted — check the low and high seat.",
	EVENT_NOT_FOUND: "Gametime cannot find this event — check the event mapping.",
};

export const describeGametimeError = (error: TGametimeListingError): string => {
	const code = (error.code ?? "").trim().toUpperCase();
	const hint = code ? ERROR_HINTS[code] : undefined;
	const message = (error.message ?? "").trim();
	const detail = error.field ? ` (${error.field}${error.value ? ` = ${error.value}` : ""})` : "";

	if (hint) return `${hint}${detail}`;
	if (message) return `${message}${detail}`;
	return code ? `Gametime error ${code}${detail}` : "Gametime reported an error without a reason";
};

const isQueueFailure = (syncStatus: number | null | undefined): boolean =>
	syncStatus === SyncStatus.ErrorRetry || syncStatus === SyncStatus.ErrorFatal;

// Gametime does not report which live listings its curation hides, so this hint is a possibility, not a fact.
export const GAMETIME_CURATION_HINT = "Not showing? Curation may be hiding it — price is the lever";
export const GAMETIME_CURATION_DETAIL =
	"Gametime curation decides which live listings it displays and does not tell us which ones it hides. " +
	"A live listing that is not showing is usually priced above comparable inventory — lowering the price is the lever.";

export const showsGametimeCurationHint = (listing: {
	status?: string | null;
	errors?: Array<TGametimeListingError> | null;
	syncStatus?: number | null;
}): boolean =>
	getGametimeListingStatus(listing.status).tone === "live" &&
	(listing.errors?.length ?? 0) === 0 &&
	!isQueueFailure(listing.syncStatus);

const GAMETIME_EVENT_URL = "https://gametime.co/events";

export const getGametimeListingUrl = (listing: {
	gametimeEventId?: string | null;
	gametimeListingId?: string | null;
}): string | null => {
	const eventId = listing.gametimeEventId?.trim();
	const listingId = listing.gametimeListingId?.trim();
	if (!eventId || !listingId) return null;
	return `${GAMETIME_EVENT_URL}/${encodeURIComponent(eventId)}/listings/${encodeURIComponent(listingId)}`;
};

export const getGametimeQueueState = (syncStatus: number | null | undefined) => {
	const sync = getSyncStatus(syncStatus);
	return sync && sync.id !== SyncStatus.Synced ? sync : null;
};

export const matchesGametimeStatusFilter = (
	listing: { status?: string | null; errors?: Array<TGametimeListingError> | null; syncStatus?: number | null },
	filter: TGametimeStatusFilter,
): boolean => {
	if (filter === "all") return true;
	const status = getGametimeListingStatus(listing.status);
	if (filter === "unmatched") return status.isUnmatched;
	return status.isError || (listing.errors?.length ?? 0) > 0 || isQueueFailure(listing.syncStatus);
};
